import { extendTheme } from '@chakra-ui/react'

export const themePrazdroj = extendTheme({
  breakpoints: {
    sm: '550px',
    md: '768px',
    lg: '960px',
    xl: '1200px',
    '2xl': '1536px',
  },

  fonts: {
    heading: 'Open Sans',
    body: 'Open Sans',
  },

  colors: {
    black: '#000000',
    blackSemiTransparent: '#808080',
    white: '#FFFFFF',
    transparent: 'transparent',

    primary: {
      base: '#388F27',
      dark: '#1A3E13',
      light: '#DFEDDD',
      lightSemiTransparent: '#DFEDDD80',
    },

    secondary: {
      base: '#2e7420',
      transparent: '#2e742033',
    },

    accent: '#9F8852',
    accentDark: '#7A6A3D',
    accentLight: '#DBD0B8',
    accentLightContrast: '#6C5C34',
    accentTransparent: '#DBD0B81A',

    sand: '#E0CE85',

    red: '#C6243E',
    redTransparent: '#c6243f1a',

    grey: {
      neutral: {
        50: '#F3F4F5',
        300: '#D1D1D1',
        500: '#9D9D9D',
        700: '#3B3B3B',
      },
      base: '#EBEBEB',
      dark: '#DCDCDC',
    },

    yellow: '#F8CA27',

    teal: '#389663',
    success: 'green',

    header: {
      bg: '#1A3E13',
      textColor: '#FFFFFF',
      borderColor: '#E4E4E4',
    },

    headerNavigation: {
      bg: '#388F27',
    },
  },

  styles: {
    global: {
      body: {
        bg: 'grey.neutral.50',
        fontWeight: 600,
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontSize: 'xs',
        lineHeight: 6,
        outline: 'none',
        fontWeight: 600,
        color: 'black',
        py: 3,
        px: 6,
        borderRadius: 'full',
        _focus: {
          boxShadow: 'none',
        },
      },
      sizes: {
        md: {
          fontSize: 'sm',
        },
      },
      variants: {
        primary: {
          border: '1px solid',
          borderColor: 'accent',
          bg: 'accent',
          px: 8,
          py: 3,
          color: 'white',
          _hover: {
            borderColor: 'accentDark',
            bg: 'accentDark',
            _disabled: {
              bg: 'accent',
              borderColor: 'accent',
            },
          },
          _focus: {
            borderColor: 'accentDark',
            bg: 'accentDark',
            boxShadow: 'none',
          },
          _active: {
            borderColor: 'accentDark',
            bg: 'accentDark',
          },
        },
        outline: {
          borderColor: 'grey.neutral.300',
          _hover: {
            borderColor: 'accent',
            bg: 'accentTransparent',
          },
        },
        secondary: {
          border: '1px solid',
          borderColor: 'grey.neutral.500',
          bg: 'transparent',
          px: 8,
          py: 2,
          color: 'accent',
          _hover: {
            borderColor: 'accent',
            bg: 'accent',
            color: 'white',
            _disabled: {
              bg: 'transparent',
              color: 'accent',
              borderColor: 'grey.base',
            },
          },
          _focus: {
            borderColor: 'accent',
            bg: 'accent',
            color: 'white',
            boxShadow: 'none',
          },
          _active: {
            borderColor: 'accent',
            bg: 'accent',
            color: 'white',
          },
        },
        tertiary: {
          border: '1px solid',
          borderColor: 'accentTransparent',
          bg: 'accentTransparent',
          color: 'accent',
          _hover: {
            borderColor: 'accentTransparent',
            bg: 'accentTransparent',
            color: 'accent',
          },
          _focus: {
            borderColor: 'accentTransparent',
            bg: 'accentTransparent',
            color: 'accent',
            boxShadow: 'none',
          },
          _active: {
            borderColor: 'accentTransparent',
            bg: 'accentTransparent',
            color: 'accent',
          },
        },
        light: {
          bg: 'white',
          _hover: {
            borderColor: 'accent',
          },
          _focus: {
            borderColor: 'accent',
            boxShadow: 'none',
          },
          _active: {
            borderColor: 'accent',
          },
        },
        danger: {
          border: '1px solid',
          borderColor: 'grey.base',
          bg: '#fff',
          px: 8,
          color: 'red',
          _hover: {
            borderColor: 'red',
            bg: 'red',
            color: 'white',
            _disabled: {
              bg: '#fff',
              color: 'red',
              borderColor: 'grey.base',
            },
          },
          _focus: {
            borderColor: 'red',
            bg: 'red',
            color: 'white',
            boxShadow: 'none',
          },
          _active: {
            borderColor: 'red',
            bg: 'red',
            color: 'white',
          },
        },
        dangerSolid: {
          border: '1px solid',
          borderColor: 'red',
          bg: 'red',
          px: 8,
          color: '#fff',
          _hover: {
            borderColor: 'red',
            bg: '#fff',
            color: 'red',
            _disabled: {
              bg: 'red',
              color: '#fff',
              borderColor: 'red',
            },
          },
          _focus: {
            borderColor: 'red',
            bg: '#fff',
            color: 'red',
            boxShadow: 'none',
          },
          _active: {
            borderColor: 'red',
            bg: 'red',
            color: 'white',
          },
        },
        link: {
          fontSize: 12,
          fontWeight: 700,
          color: 'accent',
          textTransform: 'capitalize',
          _hover: {
            textDecoration: 'underline',
          },
        },
      },
      defaultProps: {
        size: 'md',
        variant: 'primary',
      },
    },
    Heading: {
      baseStyle: {
        color: 'black',
        fontWeight: 700,
      },
      sizes: {
        h1: {
          fontSize: 'xl',
        },
        h2: {
          fontSize: 'md',
        },
        h3: {
          color: 'black',
          fontSize: 'md',
        },
      },
      defaultProps: {
        size: 'h1',
      },
    },
    FormLabel: {
      baseStyle: {
        color: 'black',
        opacity: 0.5,
        fontSize: 'xs',
        fontWeight: 600,
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            fontSize: 'sm',
            fontWeight: 600,
            border: '1px solid',
            borderColor: 'grey.neutral.300',
            borderRadius: 'md',
            _hover: {
              borderColor: 'accent',
            },
            _focus: {
              borderColor: 'accent',
              boxShadow: 'none',
            },
          },
        },
      },
    },
    NumberInput: {
      variants: {
        outline: {
          field: {
            fontSize: 'sm',
            fontWeight: 600,
            border: '1px solid',
            borderColor: 'grey.neutral.300',
            borderRadius: 'md',
            _hover: {
              borderColor: 'accent',
            },
            _focus: {
              borderColor: 'accent',
              boxShadow: 'none',
            },
          },
        },
      },
    },
    Textarea: {
      variants: {
        outline: {
          fontSize: 'sm',
          fontWeight: 600,
          border: '1px solid',
          borderColor: 'grey.neutral.300',
          borderRadius: 'md',
          _hover: {
            borderColor: 'accent',
          },
          _focus: {
            borderColor: 'accent',
            boxShadow: 'none',
          },
        },
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            fontSize: 'sm',
            fontWeight: 600,
            border: '1px solid',
            borderColor: 'grey.neutral.300',
            borderRadius: 'md',
            _hover: {
              borderColor: 'accent',
            },
            _focus: {
              borderColor: 'accent',
              boxShadow: 'none',
            },
          },
        },
      },
    },
    Checkbox: {
      sizes: {
        md: {
          icon: {
            height: 2,
          },
        },
        xl: {
          control: {
            width: 5,
            height: 5,
          },
          icon: {
            height: 2,
          },
        },
      },
      variants: {
        default: {
          control: {
            fontSize: 'sm',
            fontWeight: 600,
            border: '1px solid',
            borderColor: 'grey.neutral.300',
            borderRadius: 'md',
            _focus: {
              boxShadow: 'none',
              borderColor: 'secondary.base',
            },
            _checked: {
              borderColor: 'secondary.base',
              backgroundColor: 'secondary.base',
            },
          },
          icon: {
            color: 'white',
          },
        },
        round: {
          control: {
            border: '1px solid',
            borderColor: 'grey.neutral.500',
            borderRadius: 'full',
            _focus: {
              boxShadow: 'none',
              borderColor: 'primary.base',
            },
            _checked: {
              borderColor: 'primary.base',
              backgroundColor: 'primary.base',
            },
          },
          icon: {
            color: 'white',
          },
        },
      },
      defaultProps: {
        size: 'xl',
        colorScheme: 'transparent',
        variant: 'default',
      },
    },
    Modal: {
      baseStyle: {
        header: {
          fontWeight: 900,
          px: 10,
          pt: 10,
        },
        body: {
          px: 10,
          pb: 10,
        },
        footer: {
          px: 10,
          pb: 10,
          pt: 0,
        },
        closeButton: {
          top: 4,
          right: 4,
          _focus: {
            boxShadow: 'none',
          },
        },
      },
      defaultProps: {
        size: '3xl',
      },
    },
    Tooltip: {
      baseStyle: {
        fontWeight: 600,
        bg: 'black',
        color: 'white',
        borderRadius: 'md',
        fontSize: 'sm',
      },
    },
    Menu: {
      baseStyle: {
        button: {
          _hover: {
            borderColor: 'red',
            bg: 'white',
          },
          _active: {
            borderColor: 'red',
            bg: 'white',
          },
        },
        list: {
          py: 0,
          overflow: 'hidden',
          borderColor: 'grey.base',
        },
        item: {
          fontWeight: 600,
          bg: 'white',
          color: 'black',
          fontSize: '14px',
          py: 4,
          pl: 6,
          pr: 4,
          _hover: {
            bg: 'accentTransparent',
            color: 'accent',
          },
          _focus: {
            bg: 'accentTransparent',
            color: 'accent',
          },
        },
        divider: {
          my: 0,
          ml: 6,
          mr: 1,
          borderColor: 'grey.base',
        },
      },
      defaultProps: {
        autoSelect: false,
      },
    },
    Tabs: {
      baseStyle: {
        indicator: {
          bg: 'transparent',
        },
      },
      variants: {
        line: {
          root: {
            borderColor: 'transparent',
          },
          tab: {
            borderBottomWidth: '1px',
            borderColor: 'transparent',
            color: 'accent',
            px: 6,
            _focus: {
              outline: 'none',
              boxShadow: 'none',
              bg: 'white',
            },
            _hover: {
              bg: 'accentTransparent',
              borderColor: 'accent',
            },
            _active: {
              bg: 'accentTransparent',
              borderColor: 'accent',
            },
            _selected: {
              bg: 'accentTransparent',
              borderColor: 'accent',
            },
          },
        },
        rounded: {
          root: {
            borderColor: 'transparent',
          },
          tab: {
            fontWeight: 600,
            borderBottomWidth: 0,
            color: 'white',
            py: 2,
            px: 5,
            borderRadius: 9999,
            _focus: {
              outline: 'none',
              boxShadow: 'none',
              bg: 'transparent',
            },
            _hover: {
              bg: '#1A3E1352',
              color: 'white',
            },
            _active: {
              bg: '#1A3E1352',
              color: 'white',
            },
            _selected: {
              bg: '#1A3E1352',
              color: 'white',
            },
          },
        },
        coloredRounded: {
          root: {
            borderColor: 'transparent',
          },
          tab: {
            fontSize: 15,
            fontWeight: 600,
            borderBottomWidth: 0,
            color: 'accentLightContrast',
            py: 2,
            px: 5,
            borderRadius: 9999,
            _focus: {
              outline: 'none',
              boxShadow: 'none',
              bg: 'accentLight',
            },
            _hover: {
              bg: 'accentTransparent',
              color: 'accentLightContrast',
            },
            _active: {
              bg: 'accentLight',
              color: 'accentLightContrast',
            },
            _selected: {
              bg: 'accentLight',
              color: 'accentLightContrast',
            },
          },
        },
        unstyled: {
          root: {
            borderColor: 'transparent',
          },
          tab: {
            color: 'blackSemiTransparent',
            bg: 'white',
            borderBottomWidth: '1px',
            borderColor: 'transparent',
            px: 0,
            mr: 8,
            _focus: {
              outline: 'none',
              boxShadow: 'none',
              bg: 'white',
            },
            _hover: {
              bg: 'white',
              color: 'accent',
              borderColor: 'accent',
            },
            _active: {
              bg: 'white',
              borderColor: 'accent',
            },
            _selected: {
              color: 'accent',
              borderColor: 'accent',
            },
          },
        },
      },
      defaultProps: {
        colorScheme: 'accent',
      },
    },
    Radio: {
      variants: {
        primary: {
          control: {
            fontSize: 'sm',
            fontWeight: 600,
            _checked: {
              backgroundColor: 'accent',
              borderColor: 'accent',
            },
            _focus: {
              boxShadow: 'none',
            },
          },
          label: {
            fontSize: 'sm',
          },
        },
      },
      defaultProps: {
        variant: 'primary',
        colorScheme: 'primary',
        size: 'lg',
        borderRadius: 'full',
      },
    },
  },
})
